import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import { containsCoordinate } from 'ol/extent';
import { DragPan, DragBox } from 'ol/interaction';
import React, { useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import styles from './index.module.scss';
import Mappers from './Mappers';

import actions from '@/actions';
import Cross from '@/components/ui/map/Cross';
import OlMap from '@/helpers/OlMap';

const cx = classNames.bind(styles);

const Map = ({ onInitialized }) => {
  const dispatch = useDispatch();
  const focusRobotId = useSelector((state) => state.robot.focusRobotId);
  const store = useStore();
  const map = useRef();
  const mapRef = useRef();
  const isShiftKeyPressed = useRef(false);

  useMountEffect(() => {
    initMap();
    initDragBox();

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
    };
  });

  useUpdateEffect(() => {
    const dragPan = map.current
      .getInteractions()
      .getArray()
      .find((interaction) => interaction instanceof DragPan);

    // Focus 설정된 로봇이 존재하지 않는 경우 지도 drag 허용
    dragPan.setActive(!focusRobotId);

    if (!focusRobotId) return;

    // 로봇 위치로 지도 이동
    moveToRobot();

    const robot = map.current.getOverlayById(`robots/${focusRobotId}`);
    robot.on('change:position', moveToRobot); // 로봇 위치 변경 시

    return () => {
      robot.un('change:position', moveToRobot);
    };
  }, [focusRobotId]);

  const initMap = () => {
    // redux-persist 활용한 최종 center 및 zoom 적용
    const { center, zoom } = store.getState().map;
    map.current = OlMap.init(mapRef.current, { center, zoom });

    // 맵 초기화 완료 알림
    if (onInitialized) {
      onInitialized();
    }

    setTimeout(() => OlMap.fitBoundsAuto(), 1000);

    // 지도 이동 중일 시
    map.current.getView().on('change:center', () => {
      const { lat, lng } = OlMap.getCenter();
      dispatch(actions.map.setCenter(lat, lng));
    });

    // 지도 이동 또는 줌 완료 시
    map.current.on('moveend', () => {
      // 로봇 Focus 설정된 경우
      if (store.getState().robot.focusRobotId) {
        // 로봇 위치로 지도 이동
        moveToRobot();
      }

      const { lat, lng } = OlMap.getCenter();
      const zoom = map.current.getView().getZoom();
      dispatch(actions.map.setPositionWithZoom(lat, lng, zoom));
    });

    // 지도 클릭 시
    map.current.on('click', (e) => {
      const { sidebarCommandId, overlayCommandId } = store.getState().command;
      if (sidebarCommandId || overlayCommandId) return;

      const robotId = e.originalEvent.target.dataset.robotId;
      // 지도상 로봇 클릭 시
      if (robotId) {
        const robot = store.getState().robots.find(({ id }) => id === robotId);
        // 소유 로봇인 경우
        if (robot?.isOwned) return;
        // 로봇 클릭 핸들러는 각 로봇 마커에서 정의
      }

      const isModifierPressed = e.originalEvent.ctrlKey || e.originalEvent.metaKey;
      // 수정자 키 조합 클릭 시
      if (isModifierPressed) return;

      dispatch(actions.robot.reset());
    });
  };

  const initDragBox = () => {
    const dragBox = new DragBox({
      condition: (e) => {
        const robotId = e.originalEvent.target.dataset.robotId;
        return !robotId && isShiftKeyPressed.current;
      },
      className: cx('dragbox'),
    });

    dragBox.on('boxend', (e) => {
      // 수정자 키 조합 여부
      // - ctrlKey: Windows/Linux의 Ctrl 키
      // - metaKey: macOS의 Cmd 키 (⌘)
      const isModifierPressed = e.mapBrowserEvent.originalEvent.ctrlKey || e.mapBrowserEvent.originalEvent.metaKey;
      // 수정자 키 조합 없이 단일 선택 시
      if (!isModifierPressed) {
        dispatch(actions.robot.reset());
      }

      const extent = dragBox.getGeometry().getExtent();
      const selectedRobotIds = Object.entries(OlMap.getRobots())
        .filter(([, overlay]) => {
          const coordinate = overlay.getPosition();
          return containsCoordinate(extent, coordinate);
        })
        .map(([robotId]) => robotId);

      toggleRobots(selectedRobotIds);
    });

    map.current.addInteraction(dragBox);
  };

  const toggleRobots = (robotIds) => {
    const { robots } = store.getState();
    const ownedRobotIdSet = new Set(robots.filter(({ isOwned }) => isOwned).map(({ id }) => id));
    const ownedRobotIds = robotIds.filter((robotId) => ownedRobotIdSet.has(robotId));

    ownedRobotIds.forEach((robotId) => {
      dispatch(actions.robot.toggle(robotId));
    });
  };

  const moveToRobot = () => {
    const currentFocusRobotId = store.getState().robot.focusRobotId;
    const robot = map.current.getOverlayById(`robots/${currentFocusRobotId}`);
    const center = robot.getPosition();

    map.current.getView().animate({ center, duration: 0 });
  };

  const onKeyDown = (e) => {
    if (e.shiftKey) {
      isShiftKeyPressed.current = true;
    }

    // Windows/Linux의 Ctrl 키
    // macOS의 Cmd 키 (⌘)
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        // a 키 조합 시 : 전체 선택
        case 'a':
          dispatch(actions.robot.reset());

          store
            .getState()
            .robots.filter(({ isActive }) => isActive)
            .forEach(({ id }) => dispatch(actions.robot.toggle(id)));
          break;

        // esc 키 조합 시 : 전체 선택 해제
        case 'Escape':
          dispatch(actions.robot.reset());
          break;

        default:
          break;
      }
    }
  };

  const onKeyUp = (e) => {
    if (!e.shiftKey) {
      isShiftKeyPressed.current = false;
    }
  };

  return (
    <div className={cx('container')}>
      <div ref={mapRef} className={cx('map')} />
      <Mappers />
      <Cross />
    </div>
  );
};

export default Map;
